import React, { useCallback, useState, forwardRef } from 'react';
import styled from 'styled-components';
import { white } from '@zocdoc-mezzanine/foundations-colors';
import { FormErrorMessage } from '@zocdoc-mezzanine/internal-shared';
import usePropValidation from './usePropValidation';

/**
 * BaseTextfield
 * @param {TextfieldProps} props - Props for the textfield.
 * @returns {JSX.Element} BaseTextfield
 */
const BaseTextfield = forwardRef((props, ref) => {
    const {
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledby,
        'aria-describedby': ariaDescribedby,
        placeholder,
        autocomplete,
        inputmode,
        pattern,
        id,
        name,
        value,
        labelText,
        disabled,
        maxlength,
        invalidMessage,
        required,
        onChange,
        onFocus,
        onBlur,
        type,
        leftIcon,
        rightIcon,
        className,
        'data-test': dataTest,
        'data-field-id': dataFieldId,
    } = props;

    usePropValidation({
        labelText,
        id,
        ariaLabel,
        ariaLabelledby,
        ariaDescribedby,
        onChange,
        type,
    });

    const [inputHasFocus, setInputHasFocus] = useState(false);

    const onFocusAction = useCallback(
        (e) => {
            setInputHasFocus(true);

            if (typeof onFocus === 'function') {
                onFocus(e);
            }
        },
        [onFocus]
    );

    const onBlurAction = useCallback(
        (e) => {
            setInputHasFocus(false);

            if (typeof onBlur === 'function') {
                onBlur(e);
            }
        },
        [onBlur]
    );
    const dataTestPrefix = dataTest || 'textfield';

    return (
        <Wrapper className={className}>
            {labelText && (
                <label
                    htmlFor={id}
                    id={`${id}-label`}
                    data-test={`${dataTestPrefix}-label`}
                >
                    {labelText}
                </label>
            )}
            <InputWrapper
                data-test={`${dataTestPrefix}-wrapper`}
                data-invalid={!!invalidMessage}
                // Used for the variants to style outlines and exclude clickable icons from getting the overlay.
                data-input-has-focus={inputHasFocus}
                $disabled={disabled}
            >
                {leftIcon && (
                    <LeftIconWrapper data-test={`${dataTestPrefix}-left-icon`}>
                        {leftIcon}
                    </LeftIconWrapper>
                )}
                <StyledTextfield
                    aria-describedby={ariaDescribedby}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledby}
                    data-test={`${dataTestPrefix}-input`}
                    data-field-id={dataFieldId}
                    ref={ref}
                    id={id}
                    pattern={pattern}
                    autoComplete={autocomplete}
                    inputMode={inputmode}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    maxLength={maxlength}
                    name={name}
                    required={required}
                    onChange={onChange}
                    onFocus={onFocusAction}
                    onBlur={onBlurAction}
                    type={type}
                />
                {rightIcon && (
                    <RightIconWrapper
                        data-test={`${dataTestPrefix}-right-icon`}
                    >
                        {rightIcon}
                    </RightIconWrapper>
                )}
            </InputWrapper>
            {invalidMessage && (
                <FormErrorMessage
                    data-test={`${dataTestPrefix}-error`}
                    invalidMessage={invalidMessage}
                />
            )}
        </Wrapper>
    );
});

const Wrapper = styled.span`
    box-sizing: border-box;
`;

export const InputWrapper = styled.span`
    display: flex;
    width: 100%;
    background-color: ${white};

    ${({ $disabled }) =>
        $disabled &&
        `
        &:hover {
            cursor: auto;
        }
    `}
`;

export const LeftIconWrapper = styled.span`
    overflow: hidden;
`;

export const RightIconWrapper = styled.span`
    overflow: hidden;
`;

const StyledTextfield = styled.input`
    outline: 0;
    border: none;
    width: 100%;
`;

export default BaseTextfield;
